// @atlas/contracts — a `tickets` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { TICKET_STATUSES, TICKET_TYPES } from '@atlas/shared';
import { z } from 'zod';
import { nullableStr, PersonOptionSchema } from '../_shared/schemas.js';
/**
 * A jegy státusza és típusa ÍRÁSKOR — az értékkészlet a `@atlas/shared` `ticket-enums.ts`-éből (az Odin registry
 * ugyanezt az enumot validálja). A sor-séma (`TicketRowSchema`) olvasáskor szándékosan `z.string()`: egy
 * ismeretlen tárolt érték ne ejtse el a teljes listát.
 */
export const TicketStatusSchema = z.enum(TICKET_STATUSES);
export const TicketTypeSchema = z.enum(TICKET_TYPES);
/**
 * Ticket-sor (support lista-nézet, belső): a `support.tickets` [T] busz-erőforrás fehérlistás mezői
 * (subject/status/type/…) + a kapcsolt entitások BFF-oldali feloldása (fiók-név, assignee/requester
 * identity-név, support-csapat neve az i18n JSON-ból). A `commentCount` a `support.ticket-comments`
 * busz-erőforrás EGYSZERI batch-lehúzásából, ticketId-re csoportosítva (nem per-soros N+1). Az
 * `accountId`/`assigneeId`/`requesterId`/`supportTeamId` a nyers UUID (a UI a *-Name mezőt jeleníti).
 */
export const TicketRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    /** A fiókot birtokló ügyfél neve (BFF-merge, foundation `/v1/admin/clients`) — a fiók-név alatt jelenik meg. */
    clientName: z.string().nullable(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    ticketNumber: z.string().nullable(),
    subject: z.string(),
    /**
     * A leírás EGY SOROS, SIMA SZÖVEG kivonata (a lista-cellában a tárgy alatt, halványabban) — a partner-lista
     * mintája (user-döntés). A tárolt törzs rich-text HTML: a kivonatot a BFF strippeli
     * (`plainTextExcerpt`), tehát a válaszban SOHA nincs tag — a cella `dangerouslySetInnerHTML` nélkül rendereli.
     */
    descriptionExcerpt: z.string(),
    /** open | in_progress | resolved | closed | reopened (a legacy enum 1:1). */
    status: z.string(),
    /** support | bug | feature_request | billing | other. */
    type: z.string(),
    assigneeId: z.string().nullable(),
    assigneeName: z.string().nullable(),
    /** A felelős név-részei + avatarja (avatar + nyelvhelyes név a lista-cellában). */
    assigneeGivenName: z.string().nullable(),
    assigneeFamilyName: z.string().nullable(),
    assigneeAvatarUrl: z.string().nullable(),
    /** A felelős kolléga csapat-színe (szín-token) — az avatar háttere; nincs felelős / nincs csapat → NULL. */
    assigneeTeamColor: z.string().nullable(),
    requesterId: z.string().nullable(),
    requesterName: z.string().nullable(),
    /** Az igénylő név-részei + avatarja. */
    requesterGivenName: z.string().nullable(),
    requesterFamilyName: z.string().nullable(),
    requesterAvatarUrl: z.string().nullable(),
    supportTeamId: z.string().nullable(),
    supportTeamName: z.string().nullable(),
    createdAt: z.string().nullable(),
    /**
     * A LEZÁRÁS ideje — amikor a jegy a lezárt csoportba (`resolved` | `closed`) lépett. Az Odin bélyegzi
     * (`statusStamp`), újranyitáskor NULL; régi lezárt jegynél NULL, ha az ideje nem állítható helyre.
     */
    closedAt: z.string().nullable(),
    /** A lezárás oka (closeReason) — a szerkesztő-form előtöltéséhez; lista nem jeleníti. */
    closeReason: z.string().nullable(),
    /** A tickethez tartozó (belső+külső) kommentek darabszáma — a `ticket-comments` batch-lehúzásból. */
    commentCount: z.number(),
    /**
     * Ebből a BELSŐ jegyzetek száma — a lista jelzi, ha a jegyen van belső jegyzet (a jegyen belül a fül számlálója
     * mutatta, a listán semmi). Csak a belső felület sémája hordozza; a partner-portál sora SOHA.
     */
    internalNoteCount: z.number(),
    /** OCC-verzió (a busz-sor `version`-je) — a szerkesztés `If-Match`-éhez. */
    version: z.number(),
});
/**
 * Ticket-detail (belső nézet) válasz: a lista-sor MINDEN mezője (`TicketRowSchema`) + a részletező-oldalon
 * megjelenő pluszmezők: a teljes `description`, a három online-tárhely-link + az Asana-feladat URL. A
 * `closeReason`/`version` már a lista-sorban benne van (a szerkesztő-form előtöltéséhez / OCC-hez). A
 * csatolmányok KÜLÖN, későbbi fázis (most nincs). A `commentCount` a lista-sorral azonos szemantikájú.
 */
export const TicketDetailSchema = TicketRowSchema.extend({
    /** A ticket teljes leírása (a nyitáskor rögzített, később nem szerkeszthető szöveg). */
    description: z.string().nullable(),
    /** Online-tárhely-linkek (opcionálisak, a nyitáskor megadva). */
    googleDriveLink: z.string().nullable(),
    microsoftOnedriveLink: z.string().nullable(),
    dropboxLink: z.string().nullable(),
    /** A kapcsolódó Asana-feladat URL-je (opcionális). */
    asanaTaskUrl: z.string().nullable(),
    /** A fiók weboldala (BFF-merge a `clients/account` busz-sorból) — a detailen link; NULL, ha nincs/nem oldható fel. */
    accountWebsite: z.string().nullable(),
});
/**
 * Ticket-komment sor (belső nézet): a `support.ticket-comments` [T] busz-sor fehérlistás mezői + a szerző
 * BFF-oldali feloldása (`authorName`/`authorAvatarUrl` a foundation `/v1/admin/identities`-ből, a lista-
 * dúsítás mintájára). A belső felület MINDKÉT `kind`-et (internal+external) mutatja; a partner-szűrés (csak
 * external) a jövőbeli partner-felület dolga. A `ticketId`/`kind`/`body` a nyers busz-mező.
 */
/**
 * KI reagált — a „ki kedvelte" felugró listájának egy sora. A NÉV-RÉSZEK azért mennek külön, mert a
 * névsorrend nyelv-függő, és azt a kliens dönti el (`formatName`) — ugyanaz a minta, mint a komment
 * szerzőjénél.
 */
export const TicketReactionActorSchema = z.object({
    /**
     * A reagáló belső azonosítója — **a PARTNER-válaszban SZÁNDÉKOSAN hiányzik** (ezért opcionális).
     *
     * A felület a saját reakció felismeréséhez korábban ezt hasonlította a hívó `sub`-jához, ami azt
     * jelentette, hogy a KOLLÉGÁK nyers azonosítói is kimentek a partnernek — miközben a partner-doksi §6 azt
     * mondja ki, hogy belső azonosító SOHA nem megy ki, csak a név és a profilkép. A felismerést azóta az
     * `isMe` végzi, amit a szerver tölt ki: így a viselkedés változatlan, az azonosító viszont bent marad.
     */
    identityId: z.string().optional(),
    /** A HÍVÓ saját reakciója-e ez a sor. A szerver dönti el — a kliensnek nem kell azonosítót hasonlítania. */
    isMe: z.boolean(),
    name: z.string().nullable(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    /** A kolléga csapat-színe — az avatar háttere a „ki reagált" listában (partnernél null). */
    teamColor: z.string().nullable(),
});
/**
 * EGY emoji összesítése egy kommenten: az emoji, a darabszám, hogy a HÍVÓ benne van-e, és KIK reagáltak.
 *
 * MIÉRT MEGY A NÉV-LISTA a lista-válaszban (és nem külön végponton): a felugró „ki kedvelte" panelnek
 * azonnal kell a tartalom (a hover/koppintás nem várhat egy kérésre), a kör pedig KICSI — a reakciók száma
 * egy belső komment-szálon néhány tucat, és a névfeloldás amúgy is megvan a lista-dúsításhoz (ugyanaz a
 * identity-térkép, amiből a komment szerzője jön). Egy külön végpont N+1 kérést hozna a szálra.
 */
export const TicketCommentReactionSchema = z.object({
    emoji: z.string(),
    count: z.number(),
    /** A HÍVÓ adta-e ezt az emojit (a toggle-hoz és a kiemelt állapothoz). */
    reactedByMe: z.boolean(),
    actors: z.array(TicketReactionActorSchema),
});
export const TicketCommentRowSchema = z.object({
    id: z.string(),
    ticketId: z.string(),
    /** internal (belső jegyzet) | external (a partnerrel is megosztott komment). */
    kind: z.enum(['internal', 'external']),
    body: z.string(),
    /** A szerző identity-je (a komment írásakor a hívó token `sub`-ja) — nyers UUID; NULL, ha ismeretlen. */
    authorIdentityId: z.string().nullable(),
    /** A szerző megjelenítendő neve (feloldva) — NULL, ha nem oldható fel. */
    authorName: z.string().nullable(),
    /**
     * A szerző NÉV-RÉSZEI — a névsorrend NYELV-FÜGGŐ (magyarul vezetéknév elöl, más nyelven keresztnév elöl),
     * és ezt a kliens dönti el a saját locale-ján. A `displayName` erre nem alkalmas: egy előre összefűzött
     * névből nem lehet visszafejteni a részeket. Ugyanez a minta minden személy-oszlopon (`PersonChip`).
     */
    authorGivenName: z.string().nullable(),
    authorFamilyName: z.string().nullable(),
    /** A szerző avatar-URL-je (ha olcsón feloldható a lista-dúsításból) — opcionális. */
    authorAvatarUrl: z.string().nullable().optional(),
    /** A szerző csapat-színe (kolléga) — az avatar háttere; partnernél NULL. */
    authorTeamColor: z.string().nullable().optional(),
    /**
     * A szerző ISMERT munkatárs-e (belső identity) — a szál „mi vs. ügyfél" tónusa ebből (Évi #84). Ismeretlen /
     * törölt szerző → `false` (semleges buborék: a „mi" tónust csak ismert kolléga kapja).
     */
    authorIsColleague: z.boolean(),
    createdAt: z.string().nullable(),
    /**
     * A SZÖVEG utolsó szerkesztésének ideje (`null` = sosem szerkesztették) — a „Szerkesztve" jelölés forrása. Az
     * Odin bélyegzi (szerver-kezelt): a kliens nem írhatja, tehát a jelölés nem tüntethető el.
     */
    editedAt: z.string().nullable(),
    /** OCC-verzió (a busz-sor `version`-je) — jelenleg csak a teljesség kedvéért. */
    version: z.number(),
    /**
     * EMOJI-REAKCIÓK az adott kommenten, emojink ÖSSZEVONVA (darabszám + a reagálók listája). Üres tömb, ha
     * senki nem reagált. A sorrend a szerver-oldali: elöl a legtöbbet használt emoji (holtversenyben a
     * korábbi első reakció) — így a lista nem ugrál kérésenként.
     */
    reactions: z.array(TicketCommentReactionSchema),
});
/**
 * Reakció TOGGLE body: CSAK az emoji. A kommentet a path azonosítja, a reagáló a hívó token `sub`-ja (SOHA
 * nem kliens-input — különben más nevében lehetne „kedvelni").
 *
 * Az EMOJI-KÉSZLET KÖTÖTT, de BŐ (30 jel, három csoportban: visszajelzés · érzelem · munka-jelzés) —
 * user-kérés (két körben bővítve: 6 → 18 → 30).
 *
 * MIÉRT KÖTÖTT ÉS NEM SZABAD SZÖVEG: (a) egy szabad-szöveges emoji-mező tetszőleges tartalmat (akár teljes
 * mondatot) engedne a reakció-sávba — a szerver 422-vel áll ellen; (b) a fix készlet i18n- és a11y-oldalon
 * kezelhető (minden jelnek van felirata); (c) nem kell hozzá ~1 MB-os emoji-adatbázis a bundle-be (a
 * `emoji-mart`/`emoji-picker-react` ára), miközben a valós használat néhány jelre koncentrálódik.
 * Új emoji = itt egy sor + a felület `REACTION_CHOICES`-a (a fal: `ticket-reaction-emojis.wall.spec.ts`).
 */
export const TICKET_REACTION_EMOJIS = [
    // VISSZAJELZÉS (a napi használat java)
    '👍',
    '👎',
    '✅',
    '❌',
    '👀',
    '🙏',
    '💪',
    '🤝',
    // ÉRZELEM
    '❤️',
    '🎉',
    '😀',
    '😂',
    '😍',
    '😮',
    '😢',
    '😡',
    '🤯',
    '😴',
    // MUNKA-JELZÉS
    '🔥',
    '💯',
    '🚀',
    '🤔',
    '⚠️',
    '🐛',
    '📌',
    '⏰',
    '💡',
    '🧠',
    '📎',
    '🔒',
];
export const TicketReactionToggleBodySchema = z.object({
    emoji: z.enum(TICKET_REACTION_EMOJIS),
});
/**
 * Ticket-komment létrehozás body — CSAK a `kind` + a `body`. A `ticketId` a path-ból, az `accountId`/cél-
 * tenant a ticket FORRÁSÁBÓL (szerver-oldalon feloldva), a `authorIdentityId` a hívó token `sub`-ja (SOHA
 * nem kliens-input — IDOR-védelem). A `body` min. 1 karakter trim után (a „nem-üres" a valódi invariáns; a
 * legacy min:3 önkényes volt, a ticket-tárgy min:1-ével egységesítve).
 */
export const TicketCommentCreateBodySchema = z.object({
    kind: z.enum(['internal', 'external']),
    body: z.string().trim().min(1).max(20000),
});
/**
 * Ticket-szerkesztés (belső menedzsment) body — a `support/tickets` [T] busz-sor SZERKESZTHETŐ mezői (a legacy
 * 1:1): státusz + lezárás-ok + felelős + support-csapat. A tárgy/leírás/típus a nyitás után NEM szerkeszthető
 * (a legacy is így), a create KÜLÖN (typeahead-pickerekkel, `#TKT-` számmal). A státusz-átmeneteket + a
 * `closeReason` kötelezőségét (csak `closed`-nál) a BFF kényszeríti (422). Az `undefined` mező érintetlen.
 */
export const TicketUpdateBodySchema = z.object({
    status: TicketStatusSchema,
    closeReason: nullableStr(4000),
    assigneeId: z.string().nullable().optional(),
    supportTeamId: z.string().nullable().optional(),
    /**
     * Elküldjük-e a lezárás okát a PARTNERNEK a lezáró e-mailben? **Default: NEM** (deny-by-default) — a
     * `closeReason` gyakran belső megjegyzés, ezért a megosztás tudatos, egyszeri döntés a lezáró képernyőn
     * (a legacy `sendCloseReasonInEmail` pipája). A mező csak az ÉRTESÍTÉST befolyásolja, a tárolt sort nem.
     */
    shareCloseReason: z.boolean().optional(),
});
/** Ticket-szerkesztő opció-listák (felelős = belső identity-k; support-csapatok). Kis listák → egyszerű select. */
export const TicketRefsSchema = z.object({
    /** SZEMÉLY-opciók: a név-részek is jönnek — a névsorrend és az ABC-rend a KLIENSEN, nyelv szerint dől el. */
    assignees: z.array(PersonOptionSchema),
    /**
     * A csapatok + a TAGJAIK (identity-id-k) — a felelős-választó ebből állítja be a csapatot (Évi #83). Olvasási
     * hibánál a `memberIds` üres (a választó működik, a csapat automatikus beállítása elmarad).
     */
    supportTeams: z.array(z.object({ id: z.string(), name: z.string(), memberIds: z.array(z.string()) })),
});
/**
 * Ticket létrehozás ("Új ticket", belső) body — a legacy 1:1 mezői + szabályai: fiók (kötelező), igénylő
 * (kapcsolattartó → opened_by), típus, tárgy (≤255), leírás (**min. 25 karakter** — a legacy szabály), online-
 * linkek (opcionális), felelős/csapat (opcionális; a legacy auto-assign HELYETT explicit — flagged). A státusz
 * fixen `open`, a `ticketNumber`-t a BFF generálja (legacy-hű random `#TKT-{év}/{6 hex}_AGENT`). A cél-tenantot a
 * BFF a FIÓK forrásából oldja fel (a kliens nem küld clientId-t). Az értesítés/komment KÜLÖN (e-mail-fázis).
 */
/**
 * Egy szolgáltatás-opció a ticket-nyitó formon. A `name` MÁR lokalizált (a BFF a kérés nyelvén adja) — a
 * kliens nem lát nyers i18n-objektumot.
 *
 * A `supportTeamName` KIZÁRÓLAG MEGJELENÍTÉS: megmutatja, MELYIK csapathoz kerül a jegy, ha ezt a
 * szolgáltatást választják. A routing maga továbbra is SZERVER-OLDALI (a create újra feloldja a
 * szolgáltatásból) — a kliens sem csapatot, sem felelőst nem küld, tehát ez nem kliens-input.
 */
export const TicketServiceOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
    supportTeamName: z.string().nullable(),
});
/**
 * Egy igénylő-opció (a FIÓK kapcsolattartói) — a legacy „kinek a nevében" listája. A SZEMÉLY-opció mag
 * (`PersonOptionSchema`) + az e-mail, mert az igénylő azonosításához a név mellett az e-mail is kell
 * (névazonos kapcsolattartók).
 */
export const TicketRequesterOptionSchema = PersonOptionSchema.extend({
    email: z.string().nullable(),
});
/**
 * A ticket-nyitó form fiók-FÜGGŐ opciói (a legacy `updatedSelectedAccount` szerver-oldali párja):
 * a szolgáltatás-lista KÉT csoportban — a fiók által VALÓBAN használt (aktív, ismétlődő díjú
 * hozzárendelésből jövő) szolgáltatások, majd a többi választható — és a fiók kapcsolattartói.
 */
export const TicketFormOptionsSchema = z.object({
    services: z.object({
        /** A fiókhoz aktívan hozzárendelt szolgáltatások (a legacy „existing" csoport). */
        existing: z.array(TicketServiceOptionSchema),
        /** A többi, ticketen választható szolgáltatás (a legacy „other" csoport). */
        other: z.array(TicketServiceOptionSchema),
    }),
    requesters: z.array(TicketRequesterOptionSchema),
});
export const TicketCreateBodySchema = z.object({
    accountId: z.string().min(1),
    /**
     * A jegy TÁRGYÁT adó szolgáltatás — a legacy nyitó-form kötelező mezője. Ez határozza meg a jegy ÚTJÁT: a
     * támogatói csapat és az első felelős a szolgáltatásból oldódik fel a szerveren (lásd `ticketCreate`).
     * A legacy a `service_id`-t NEM tárolta a `tickets` táblán (nincs is ilyen oszlopa) — mi sem tároljuk.
     */
    serviceId: z.string().min(1),
    requesterId: z.string().min(1),
    type: TicketTypeSchema,
    subject: z.string().trim().min(1).max(255),
    description: z.string().trim().min(25).max(20000),
    googleDriveLink: nullableStr(2083),
    microsoftOnedriveLink: nullableStr(2083),
    dropboxLink: nullableStr(2083),
});
export const TicketListQuerySchema = z.object({
    q: z.string().optional(),
    /** Életciklus-fül (a legacy tabjai). Üres → nincs csoport-szűrés (az Atlas „Mind" füle). */
    group: z.enum(['in_progress', 'closed']).optional(),
    /** `true` → CSAK a hívóra osztott jegyek (a legacy „a saját jegyeim" alap-szűrője). */
    mine: z.coerce.boolean().optional(),
    status: z.string().optional(),
    type: z.string().optional(),
    /** Support-csapat szűrő (a csapat id-je) — a jegy `supportTeamId`-jára. */
    supportTeamId: z.string().optional(),
    /** Létrehozás-dátum tartomány (ISO date, inkluzív) a `createdAt`-ra — a lista-toolbar -tól/-ig mezői. */
    createdFrom: z.string().optional(),
    createdTo: z.string().optional(),
    /**
     * Lezárás-dátum tartomány (ISO date, inkluzív) a `closedAt`-ra — a „Lezárt" fül -tól/-ig mezői. Aktív szűrőnél
     * a lezárási idő nélküli (nyitott vagy ismeretlen idejű) jegy kiesik.
     */
    closedFrom: z.string().optional(),
    closedTo: z.string().optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/**
 * Ticket-csatolmány sor (belső nézet): a `support.ticket-attachments` [T] busz-sor fehérlistás mezői
 * (`ticketId`/`filename`/`fileId`) + a BFF-oldali dúsítás: friss presigned GET-URL (`downloadUrl`, az Odin
 * `/v1/files/support/:fileId/download-url`-ból) és a feltöltő neve (`uploadedByName`, a foundation
 * `/v1/admin/identities`-ből; az `uploadedById` a busz-injektált `createdBy`). A bájtok SOHA nem a BFF-en át:
 * a letöltés a presigned URL-lel KÖZVETLENÜL az objektum-tárból (SeaweedFS, S3-kompatibilis). A `legacyId` sosem jön.
 */
/**
 * FRISS letöltő-URL egy mellékletre (kattintáskor kérve — a listában kapott presigned URL percek alatt lejár).
 * A belső és a portál-végpont közös válasza.
 */
export const AttachmentDownloadUrlSchema = z.object({ url: z.string().url() });
export const TicketAttachmentRowSchema = z.object({
    id: z.string(),
    ticketId: z.string(),
    filename: z.string(),
    /** A `platform.file_metadata` sor id-je (a bájtok kulcsa); ETL-örökölt sornál NULL lehet. */
    fileId: z.string().nullable(),
    /** Friss presigned GET-URL (rövid TTL) a letöltéshez; NULL, ha nincs `fileId` vagy nem oldható fel. */
    downloadUrl: z.string().nullable(),
    /** A feltöltő identity-je (a busz-injektált `createdBy`) — nyers UUID; NULL, ha ismeretlen. */
    uploadedById: z.string().nullable(),
    /** A feltöltő megjelenítendő neve (feloldva) — NULL/hiányzó, ha nem oldható fel. */
    uploadedByName: z.string().nullable().optional(),
    createdAt: z.string().nullable(),
    /** OCC-verzió (a busz-sor `version`-je) — a teljesség kedvéért. */
    version: z.number(),
});
/**
 * Presigned feltöltő-URL kérése body — a fájl neve + MIME-típusa + mérete (bájt). A `sizeBytes` KÖTELEZŐ: az
 * Odin a presigned PUT-ot pontosan erre a méretre/típusra köti (nincs MIME-swap / storage-exhaustion). A
 * kiterjesztés-whitelist + a per-fájl méret-plafon + a max. 10 fájl/ticket korlát a BFF-en érvényesül (422);
 * az Odin presigned-aláírása a végső, megkerülhetetlen fal.
 */
export const TicketAttachmentUploadUrlBodySchema = z.object({
    filename: z.string().trim().min(1).max(255),
    contentType: z.string().trim().min(1).max(255),
    sizeBytes: z.number().int().positive(),
});
/** Presigned feltöltő-URL válasz: a PUT-URL (közvetlenül az objektum-tárba, SeaweedFS) + a létrejött `file_metadata` id (`fileId`) + a tár-kulcs. */
export const TicketAttachmentUploadUrlResponseSchema = z.object({
    uploadUrl: z.string(),
    fileId: z.string(),
    key: z.string(),
});
/**
 * Commit body — a tárba (SeaweedFS) küldött PUT UTÁN: a `fileId` (az upload-url válaszából) + a végleges `filename`. A BFF előbb
 * commitolja az Odin fájlt, majd létrehozza a `support/ticket-attachments` sort (az `accountId` + a cél-tenant a
 * ticket FORRÁSÁBÓL, szerver-oldalon feloldva — SOHA nem kliens-input). A `fileId` plain-UUID (a busz-útba
 * interpolálódik → path-traversal choke-point → 400).
 */
export const TicketAttachmentCommitBodySchema = z.object({
    fileId: z.string().min(1),
    filename: z.string().trim().min(1).max(255),
});
/**
 * Ticket-komment SZERKESZTÉS body (SZERZŐ-scope) — a `kind` + a `body`. A szerkesztést KIZÁRÓLAG a szerző
 * végezheti: a komment `authorIdentityId`-je == a hívó token `sub`-ja (a legacy `sender_id == Auth::id()`
 * szabály), amit a BFF SZERVER-oldalon ellenőriz (idegen szerző → 403). If-Match/OCC a komment `version`-jén.
 */
export const TicketCommentUpdateBodySchema = z.object({
    kind: z.enum(['internal', 'external']),
    body: z.string().trim().min(1).max(20000),
});
/**
 * Komment-sablon sor (SHARED katalógus, belső nézet): a `support.ticket-comment-templates` [T, SHARED] busz-sor
 * fehérlistás mezői. Nincs tenant-kulcs (globális belső sablon-katalógus); olvasás minden AKTÍV BELSŐ
 * identitynek (Odin RLS: `is_internal_identity()`), írás CSAK write_all (`write:all:support`).
 */
export const TicketCommentTemplateRowSchema = z.object({
    id: z.string(),
    title: z.string(),
    content: z.string(),
    createdAt: z.string().nullable(),
    version: z.number(),
});
/**
 * Komment-sablon mentés body — `title` + `content`. updateOrCreate a `title`-re (a legacy cím-alapú
 * viselkedés megőrizve): azonos című sablon → a tartalom FRISSÜL (If-Match/OCC), különben ÚJ sor. SHARED-írás
 * (write:all:support), NO_TENANT (nincs cél-ügyfél).
 */
export const TicketCommentTemplateCreateBodySchema = z.object({
    title: z.string().trim().min(1).max(255),
    content: z.string().trim().min(1).max(20000),
});
/**
 * Az Asana-feladat KÖTÉSÉNEK válasza (a jegy → feladat gomb) — a jegy új verziója (OCC) + a kész hivatkozás,
 * hogy a felület azonnal linket mutathasson újratöltés nélkül.
 *
 * MIÉRT NINCS KÉRÉS-BODY: a feladat TARTALMA szerver-oldali döntés (a jegy tárgya/leírása/fiókja + a cél-projekt
 * a konfigurációból) — ha a kliens küldhetné, a feladat szövege és a cél-projekt is kliens-input lenne. A legacy
 * ugyanígy egyetlen gombnyomás volt, mező-választás nélkül.
 */
export const TicketAsanaTaskSchema = z.object({
    id: z.string(),
    version: z.number(),
    /** A létrejött (vagy leválasztás után: `null`) Asana-feladat URL-je. */
    asanaTaskUrl: z.string().nullable(),
    /**
     * Csak létrehozáskor: `true` → a jegy legalább egy melléklete nem került át a feladatra (a feladat és a
     * kötés ettől még létrejött — a hiányzót kézzel kell csatolni).
     */
    attachmentsIncomplete: z.boolean().optional(),
});
//# sourceMappingURL=schemas.js.map